import { createAsyncThunk, createSlice } from "@reduxjs/toolkit";
import { defaultTableData } from "store/config/constants";
import { EBaseUrl } from "index";
import thunkOptions from "store/config/thunkOptions";
import {
  IPaginated,
  IState,
  IGridRequest,
  IFilterOption,
} from "store/interfaces/main";
import { api } from "store/services/apiService";
import {
    IUser,
    IUsersState,
    IChangePasswordRequest,
    IAddEditUserRequest,
    IUserCompact,
    IUserGroup,
    IViewUserRoleItem,
    IUpdateUserProfileImage,
    IAddUser,
    IActivateUser,
    IResetPasswordRequest,
    IUpdateUserRequest, IEmailRequest, IVerifyCodeRequest,
} from "../interfaces/users";

const name = "USERS";

const initialState: IUsersState = {
  data: defaultTableData,
  userGroups: null,
  userInfo: null,
  userRoles: [],
  departments: [],
};

export const GetUsers = createAsyncThunk<
  IPaginated<IUserCompact>,
  IGridRequest
>(
  `${name}/GetUsers`,
  async (data) => {
    return (await api.post(`${EBaseUrl.API}/User/Grid`, data)).data;
  },
  thunkOptions
);

export const GetDepartmentList = createAsyncThunk<any>(
  `${name}/GetDepartmentList`,
  async () => {
    return (await api.get(`${EBaseUrl.API}/User/Departments`, {})).data;
  },
  thunkOptions
);

export const GetUserById = createAsyncThunk<IUser, number>(
  `${name}/GetUserById`,
  async (userId: number) => {
    return (await api.get(`${EBaseUrl.API}/User/${userId}`)).data;
  },
  thunkOptions
);

export const GetUserGroups = createAsyncThunk<IUserGroup[]>(
  `${name}/GetUserGroups`,
  async () => {
    return (await api.get(`${EBaseUrl.API}/User/Groups`)).data;
  },
  thunkOptions
);

export const ExportUsers = createAsyncThunk<string, { userIds: number[] }>(
  `${name}/ExportUsers`,
  async (formData) => {
    return await api.post(`${EBaseUrl.API}/User/Export`, formData, {
      responseType: "blob",
    });
  }
);

export const GetFilterValues = createAsyncThunk<IFilterOption[], string>(
  `${name}/GetFilterValues`,
  async (params: string) => {
    return (await api.post(`${EBaseUrl.API}/User/Filter?${params}`, {})).data;
  },
  thunkOptions
);

export const CreateUser = createAsyncThunk<IUser, IAddUser>(
  `${name}/CreateUser`,
  async (formData) => {
    return (await api.post(`${EBaseUrl.API}/user`, formData)).data;
  },
  thunkOptions
);

export const ActivateUsers = createAsyncThunk<string, IActivateUser[]>(
  `${name}/ActivateUsers`,
  async (formData) => {
    return (await api.put(`${EBaseUrl.API}/user/CreatePassword`, formData))
      .data;
  },
  thunkOptions
);

export const EditUser = createAsyncThunk<
  IUser,
  { formData: IAddEditUserRequest; id: number }
>(
  `${name}/EditUser`,
  async ({ formData, id }) => {
    return (await api.put(`${EBaseUrl.API}/admin/users/${id}`, formData)).data;
  },
  thunkOptions
);

export const GetCurrentUser = createAsyncThunk<IUser>(
  `${name}/GetCurrentUser`,
  async () => {
    return (await api.get(`${EBaseUrl.API}/Profile`)).data;
  },
  thunkOptions
);

export const UpdateCurrentUserImage = createAsyncThunk<
  IUser,
  IUpdateUserProfileImage
>(
  `${name}/UpdateCurrentUserImage`,
  async (formData: IUpdateUserProfileImage) => {
    return (await api.put(`${EBaseUrl.API}/Profile`, formData)).data;
  },
  thunkOptions
);

export const BlockUser = createAsyncThunk<unknown, number>(
  `${name}/BlockUser`,
  async (id) => {
    return (await api.put(`${EBaseUrl.API}/admin/users/${id}/block`)).data;
  },
  thunkOptions
);

export const UnblockUser = createAsyncThunk<unknown, number>(
  `${name}/UnblockUser`,
  async (id) => {
    return (await api.put(`${EBaseUrl.API}/admin/users/${id}/unblock`)).data;
  },
  thunkOptions
);

export const UpdateUser = createAsyncThunk<unknown, IUpdateUserRequest>(
  `${name}/UpdateUser`,
  async ({ formData, id }) => {
    return (await api.put(`${EBaseUrl.API}/user/${id}`, formData)).data;
  },
  thunkOptions
);

export const ChangePassword = createAsyncThunk<unknown, IChangePasswordRequest>(
  `${name}/ChangePassword`,
  async (formData) => {
    return (await api.put(`${EBaseUrl.API}/user/ChangePassword`, formData))
      .data;
  },
  thunkOptions
);

export const SendCodeForgotPassword = createAsyncThunk<unknown, IEmailRequest>(
  `${name}/SendCodeForgotPassword`,
  async (formData) => {
    return (await api.post(`${EBaseUrl.API}/user/ForgotPassword/SendCode`, formData)).data;
  },
  thunkOptions
);

export const VerifyCodeForgotPassword = createAsyncThunk<unknown, IVerifyCodeRequest>(
    `${name}/VerifyCodeForgotPassword`,
    async (formData) => {
        return (await api.post(`${EBaseUrl.API}/user/ForgotPassword/VerifyCode`, formData)).data;
    },
    thunkOptions
);

export const ResetForgotPassword = createAsyncThunk<unknown, IResetPasswordRequest>(
    `${name}/ResetForgotPassword`,
    async (formData) => {
        return (await api.post(`${EBaseUrl.API}/user/ForgotPassword/Reset`, formData)).data;
    },
    thunkOptions
);

export const ResendCodeForgotPassword = createAsyncThunk<unknown, IEmailRequest>(
    `${name}/ResetCodeForgotPassword`,
    async (formData) => {
        return (await api.post(`${EBaseUrl.API}/user/ForgotPassword/ResendCode`, formData)).data;
    },
    thunkOptions
);

export const GetUserRoles = createAsyncThunk<IViewUserRoleItem[]>(
  `${name}/GetUserRoles`,
  async () => {
    return (await api.get(`${EBaseUrl.API}/Roles`)).data;
  },
  thunkOptions
);

const usersSlice = createSlice({
  initialState,
  name,
  reducers: {},
  extraReducers: (builder) => {
    builder.addCase(GetUsers.fulfilled, (state, { payload }) => {
      state.data = payload;
    });
    builder.addCase(GetCurrentUser.fulfilled, (state, { payload }) => {
      state.userInfo = payload;
    });
    builder.addCase(GetUserGroups.fulfilled, (state, { payload }) => {
      state.userGroups = payload;
    });
    builder.addCase(GetUserRoles.fulfilled, (state, { payload }) => {
      state.userRoles = payload;
    });
    builder.addCase(GetDepartmentList.fulfilled, (state, { payload }) => {
      state.departments = payload;
    });
  },
});

export const selectUsers = (state: IState) => state.users.data;
export const selectUserInfo = (state: IState) => state.users.userInfo;
export const selectUserGroups = (state: IState) => state.users.userGroups;
export const selectUserViewRolesList = (state: IState) => state.users.userRoles;
export const selectDepartmentsList = (state: IState) => state.users.departments;
export default usersSlice.reducer;
